// src/pages/EmbeddedCheckoutPage.js
// Route: /checkout-social?plan=individual or /checkout-social?plan=family
//
// Renders Stripe's Embedded Checkout form directly on yourscholarshipfinder.com.
// The buyer never leaves our domain — Meta pixel sees the entire journey.
//
// Part of #74 (Embedded Checkout) + #90 (Meta CAPI)
// Only used by social/ad routes. Organic flow is untouched.

import React, { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from './config';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const PLAN_DETAILS = {
  individual: {
    name: 'Individual Plan',
    price: '$47/year',
    description: 'AI-powered scholarship matching for one student'
  },
  family: {
    name: 'Family Plan',
    price: '$97/year',
    description: 'AI-powered scholarship matching for up to 3 students'
  }
};

export default function EmbeddedCheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const plan = searchParams.get('plan');
  const [error, setError] = useState(null);

  const planInfo = PLAN_DETAILS[plan];

  const fetchClientSecret = useCallback(() => {
    const fbp = document.cookie.match(/(^| )_fbp=([^;]+)/)?.[2] || '';
    const fbc = document.cookie.match(/(^| )_fbc=([^;]+)/)?.[2] || '';

    return fetch(`${API_URL}/api/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan, fbp, fbc })
    })
      .then(res => {
        if (!res.ok) throw new Error('Checkout session failed');
        return res.json();
      })
      .then(data => data.clientSecret)
      .catch(err => {
        console.error('[Checkout] Error:', err.message);
        setError(err.message);
        return '';
      });
  }, [plan]);

  if (!planInfo) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <h2 style={styles.errorTitle}>Invalid Plan</h2>
          <p style={styles.errorText}>
            Please select a valid plan from our
            <span style={styles.link} onClick={() => navigate('/social')}> scholarship matching page</span>.
          </p>
        </div>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div style={styles.container}>
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h2>Checkout Unavailable</h2>
          <p>Payment processing is temporarily unavailable. Please try again or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Complete Your Purchase</h1>
        <div style={styles.planSummary}>
          <span style={styles.planName}>{planInfo.name}</span>
          <span style={styles.planPrice}>{planInfo.price}</span>
        </div>
        <p style={styles.planDesc}>{planInfo.description}</p>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          <p>Something went wrong: {error}</p>
          <button
            style={styles.retryButton}
            onClick={() => { setError(null); window.location.reload(); }}
          >
            Try Again
          </button>
        </div>
      )}

      {!error && (
        <div style={styles.checkoutWrapper} id="checkout">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ fetchClientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}

      <div style={styles.trust}>
        <p style={styles.trustText}>
          Secure payment powered by Stripe. Your card details never touch our servers.
        </p>
        <p style={styles.trustText}>
          Cancel anytime from your account settings. Questions? Contact us at{' '}
          <a href="mailto:theteam@yourscholarshipfinder.com" style={styles.trustLink}>
            theteam@yourscholarshipfinder.com
          </a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: { textAlign: 'center', marginBottom: '32px' },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' },
  planSummary: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '8px' },
  planName: { fontSize: '18px', fontWeight: '500', color: '#333' },
  planPrice: { fontSize: '18px', fontWeight: '700', color: '#2563eb' },
  planDesc: { fontSize: '14px', color: '#666', margin: 0 },
  checkoutWrapper: { marginBottom: '32px' },
  trust: { textAlign: 'center', borderTop: '1px solid #e5e7eb', paddingTop: '20px' },
  trustText: { fontSize: '13px', color: '#888', margin: '8px 0' },
  trustLink: { color: '#2563eb', textDecoration: 'none' },
  errorCard: { textAlign: 'center', padding: '40px', border: '1px solid #e5e7eb', borderRadius: '8px' },
  errorTitle: { fontSize: '20px', color: '#1a1a1a' },
  errorText: { color: '#666' },
  link: { color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' },
  errorBanner: {
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px',
    padding: '16px', textAlign: 'center', marginBottom: '24px', color: '#991b1b'
  },
  retryButton: {
    marginTop: '8px', padding: '8px 16px', background: '#2563eb',
    color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
  }
};
